import { FC, useState } from 'react';
import cx from 'classnames';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';

import { getStripe } from 'utils/stripe';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { updateIsDone, updateStep3 } from 'redux/bookingFormSlice';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { getPendingBookingStatusAPI } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import { useIsInsuranceApplicable } from '../useIsInsuranceApplicable';
import styles from './PaymentStep.module.scss';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000; // 15 seconds

const pollForBookingId = async ({
  pendingBookingId,
  otpToken,
  tempAccessToken,
}: {
  pendingBookingId: string;
  otpToken?: string;
  tempAccessToken?: string;
}) => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    try {
      const { status, bookingId } = await getPendingBookingStatusAPI(
        pendingBookingId,
        {
          otpToken,
          tempAccessToken,
        }
      );

      if (status === 'ready' && bookingId) {
        return bookingId;
      }
    } catch {
      // Transient error — keep polling until the deadline.
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  return null;
};

const PaymentStep: FC = () => {
  const dispatch = useAppDispatch();
  const isLoadedInsideIframe = useIsLoadedInsideIframe();
  const isInsuranceApplicable = useIsInsuranceApplicable();

  const [isFinalizing, setIsFinalizing] = useState(false);

  const {
    bookingFormSlice: {
      step1: { phoneNumber },
      step3: { clientSecret, pendingBookingId, tempAccessToken, stripeAccount },
    },
  } = useAppSelector((state) => state);

  const stripePromise = getStripe(stripeAccount);

  const containerClassName = cx({
    [styles['container']]: true,
    [styles['container-iframe']]: isLoadedInsideIframe,
  });

  const handleComplete = async () => {
    if (!isInsuranceApplicable || !pendingBookingId) {
      dispatch(updateIsDone());
      return;
    }

    setIsFinalizing(true);

    try {
      const bookingId = await pollForBookingId({
        pendingBookingId,
        otpToken: loadOtpToken(phoneNumber),
        tempAccessToken,
      });

      if (bookingId) {
        dispatch(updateStep3({ bookingId }));
      }
    } finally {
      dispatch(updateIsDone());
    }
  };

  if (!clientSecret) {
    return null;
  }

  if (isFinalizing) {
    return <LoadingScreen />;
  }

  return (
    <div className={containerClassName}>
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          clientSecret,
          onComplete: handleComplete,
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default PaymentStep;
